import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_600, GRAY_700, MOBILE_VIEW_WIDTH_QUERY, WHITE } from 'constants/styling/theme'
import type { ResolvedFeaturedEntry, ResolvedShellProduct } from 'models/purchaseFlowRevamp'

import { MUIButton } from 'components/common/MUIButton'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { usePfStore } from '../../store'
import { PfRetryAlert, StepShell } from '../../components'
import { useSyncStepWithRoute } from '../../routing/useSyncStepWithRoute'
import { PropertySelectionDrawer } from '../../entry/PropertySelectionDrawer.component'
import { useEntryRecoveryDrawer } from './useEntryRecoveryDrawer'
import type { SelectedProperty } from '../../entry/useCreatePropertyInDrawer'
import { PF_STICKY_TOP, gutterBleed } from '../../components/stickyOffsets'
import { shellChrome } from '../../components/StepShell.component'
import { usePfEmbed } from '../../routing/usePfEmbed'
import { ProductDrawer } from '../../components/productDrawer'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'
import { CategorySection } from './CategorySection.component'
import { FeaturedBundleSection } from './FeaturedBundleSection.component'
import { ProductInfoDrawer } from '../../components/productInfo'
import { CategoryRadioNav } from './CategoryRadioNav.component'
import { FuzzySearchBar } from './FuzzySearchBar.component'
import { ProductCardSkeleton } from './ProductCardSkeleton.component'
import { filterCatalogue } from './useCatalogueFiltering'
import { useCategoryScrollSync } from './useCategoryScrollSync'
import { useSearchCollapseOnScroll } from './useSearchCollapseOnScroll'
import { isDocumentShell } from '../../store/documentSelectionPolicy'

/**
 * Scroll-margin applied to each category section anchor (A#2). A click-to-scroll
 * uses `scrollIntoView({ block: 'start' })`, which would otherwise place the
 * section flush with the viewport top — hidden behind ALL sticky layers: the
 * PfTopBar + progress stepper (PF_STICKY_TOP_OFFSET) and the category tab strip
 * pinned beneath them. We add the tab-strip height (~6.4rem incl. its padding +
 * divider) on top of PF_STICKY_TOP_OFFSET so the section TITLE lands clearly
 * below the navigation.
 */
const CATEGORY_ANCHOR_SCROLL_MARGIN = { xs: `calc(${PF_STICKY_TOP} + 13.2rem)`, md: `calc(${PF_STICKY_TOP} + 9rem)` }

/** The nav entry and section anchor of the Featured packs, which lead the categories when there are any. */
export const FEATURED_NAV_ID = 'featured'

/** How long the search text must stay unchanged before the search is reported. */
export const CATALOGUE_SEARCH_SETTLE_MS = 1000

/**
 * Catalogue step — browsable grid of shell products grouped by category.
 *
 * Layout (Figma 2725:9156):
 *   - Top bar: page title ("Your order") left, autosave indicator + close
 *     button right. Rendered by StepShell (sticky).
 *   - Sticky tab row: category tabs left, inline search right.
 *   - Scrollable section list — one section per category with a grid of
 *     ProductCards. IntersectionObserver keeps the active tab in sync as
 *     the user scrolls.
 *   - Right-hand cart sidebar ("Summary") with persistent "Proceed to next
 *     step" CTA. Rendered by StepShell.
 *
 * Product selection opens the ProductDrawer (Module 7) for variant / option
 * picking; committed items land in the cart, which re-opens the drawer on
 * click for edits.
 */
export const Catalogue: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const isSearchHidden = useSearchCollapseOnScroll(isMobile)
  // The mobile search folds away by sliding the pinned strip up under the top bar by the search's own height,
  // so the page layout never changes. Shrinking the strip would make scroll anchoring move the page back up,
  // which reads as an upward scroll and shows the field again (a show/hide loop).
  const searchSlotRef = useRef<HTMLDivElement | null>(null)
  const [searchSlotHeight, setSearchSlotHeight] = useState(0)
  useLayoutEffect(() => {
    const slot = searchSlotRef.current
    if (!slot || !isMobile) return
    const observer = new ResizeObserver(() => setSearchSlotHeight(slot.offsetHeight))
    observer.observe(slot)
    return () => observer.disconnect()
  }, [isMobile])
  const { isEmbed } = usePfEmbed()
  const { contentGutter: stripGutter } = shellChrome(isEmbed)

  // Keep the store's `currentStep` in sync with this route. Without it, returning
  // to the catalogue from a later step (e.g. via the footer Back) would leave
  // `currentStep` stale, so the nav bar + stepper would still show the previous
  // step's targets/labels/gating. Every other step page already does this.
  useSyncStepWithRoute('catalogue')

  // A reload drops the property, leaving an empty catalogue that never resolves.
  // The picker opens over this page instead of redirecting; `useEnsurePfV2Session`
  // self-gates on the missing property, so nothing behind the drawer misfires.
  const entryRecovery = useEntryRecoveryDrawer()
  const searchQuery = usePfStore((s) => s.searchQuery)
  const activeCategoryId = usePfStore((s) => s.activeCategoryId)
  const openDrawerForNew = usePfStore((s) => s.openForNew)

  // PF v2 session lifecycle. When a real session is required and it FAILED,
  // we must show an error+retry instead of silently rendering the mock catalogue.
  const sessionStatus = usePfStore((s) => s.draft.sessionStatus)
  const retrySession = usePfStore((s) => s.retrySession)

  const catalogueQuery = usePfCatalogue()

  // A real v2 session is required, client and admin alike — the catalogue IS the
  // session's product list. A failed create surfaces as error+retry, a pending
  // one as skeletons; neither is papered over with stand-in products.
  const showSessionError = sessionStatus === 'error'
  const isSessionResolving = sessionStatus === 'idle' || sessionStatus === 'pending'

  const handleRetrySession = useCallback(() => {
    retrySession()
  }, [retrySession])

  // An incomplete property is fixed in the property drawer, which then retries the session. Not in the
  // embed: there the property belongs to the CRM and is completed on the entry step.
  const property = usePfStore((s) => s.property)
  const isPropertyIncomplete = usePfStore((s) => s.draft.sessionStatus === 'error' && s.draft.sessionPropertyIncomplete)
  const [isCompletionDismissed, setIsCompletionDismissed] = useState(false)
  const canCompleteProperty = isPropertyIncomplete && !isEmbed && !!property?.id
  const preselectedProperty = useMemo<SelectedProperty | undefined>(() => property?.id
    ? {
      id: property.id,
      name: property.title || property.addressLabel,
      address: property.addressLabel,
      countryCode: property.countryCode,
      regionCode: property.regionCode,
      title: property.title,
      objectReferenceId: property.objectReferenceId,
    }
    : undefined, [property])

  const handleCompletionProceed = useCallback(() => {
    setIsCompletionDismissed(false)
    retrySession()
  }, [retrySession])

  const filtered = useMemo(
    () => filterCatalogue(catalogueQuery.data, searchQuery),
    [catalogueQuery.data, searchQuery]
  )

  const hasFeatured = filtered.featured.length > 0
  const navCategories = useMemo(
    () => hasFeatured
      ? [{ id: FEATURED_NAV_ID, displayName: t('purchase_flow_revamp:catalog.featured_nav') }, ...filtered.categories]
      : filtered.categories,
    [hasFeatured, filtered.categories, t]
  )

  const categoryIds = useMemo(
    () => navCategories.map((c) => c.id),
    [navCategories]
  )

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const { scrollToCategory } = useCategoryScrollSync({
    scrollContainerRef,
    categoryIds,
  })

  const trackAnalytics = usePfRevampAnalytics()

  // One event per settled search (the typing has paused), with what it found and never the text itself.
  useEffect(() => {
    if (!filtered.isSearchActive) return
    const timer = window.setTimeout(() => trackAnalytics({
      type: PfRevampEventName.CATALOGUE_SEARCHED,
      payload: {
        queryLength: searchQuery.trim().length,
        productMatches: filtered.categories.reduce((sum, category) => sum + category.products.length, 0),
        bundleMatches: filtered.featured.length,
      },
    }), CATALOGUE_SEARCH_SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [filtered, searchQuery, trackAnalytics])

  const [infoProduct, setInfoProduct] = useState<ResolvedShellProduct | null>(null)

  const handleSelectProduct = useCallback((product: ResolvedShellProduct) => {
    // Default to the first active + priced variant if available, else the first
    // active variant (drawer UI surfaces the "unavailable" state from there).
    const firstPricedActive = product.variants.find((v) => v.active && v.priced)
    const firstActive = product.variants.find((v) => v.active)
    const defaultVariant = firstPricedActive ?? firstActive ?? product.variants[0]
    if (!defaultVariant) return
    // Documents open with nothing ticked: each variant is a DIFFERENT document,
    // so a default would sell one the client never chose. Every other group is
    // variants of one product, where a default saves a click.
    openDrawerForNew(product.id, isDocumentShell(product) ? null : defaultVariant.id)
    trackAnalytics({
      type: PfRevampEventName.PRODUCT_DRAWER_OPENED,
      payload: { shellProductId: product.id, mode: 'new' },
    })
  }, [openDrawerForNew, trackAnalytics])

  /** A featured card opens the drawer on the exact product the backend featured. */
  const handleSelectFeatured = useCallback(({ shell, variantId }: ResolvedFeaturedEntry) => {
    openDrawerForNew(shell.id, variantId)
    trackAnalytics({
      type: PfRevampEventName.PRODUCT_DRAWER_OPENED,
      payload: { shellProductId: shell.id, mode: 'new' },
    })
  }, [openDrawerForNew, trackAnalytics])

  // Session error takes precedence over the catalogue query state: if the v2
  // session failed, the catalogue query is either disabled or serving the mock,
  // neither of which we want to show. `isSessionResolving` keeps skeletons up
  // until the session resolves (instead of flashing the mock).
  const isLoading = !showSessionError && (isSessionResolving || catalogueQuery.isPending)
  const isError = !showSessionError && catalogueQuery.isError

  return (
    <StepShell>

      <Box data-cy="pf-revamp-catalogue">

        <Stack gap={2}>

          {/* Sticky category row + search, the PoC's radio category row. It pins below the top bar and
              stepper. Desktop: the cards and the search share one line; a column under 600px (the
              Summary rail squeezes it between ~800 and ~1050px) puts the search above the cards.
              Mobile: the search sits on top and folds away while the page scrolls down, so the cards
              rise to the top of the screen; the cards run edge to edge under a thin rule. */}
          <Box
            data-cy="pf-revamp-category-row"
            sx={{
              position: 'sticky',
              top: isSearchHidden ? `calc(${PF_STICKY_TOP} - ${searchSlotHeight}px)` : PF_STICKY_TOP,
              transition: isMobile ? 'top 200ms ease' : undefined,
              zIndex: 8,
              backgroundColor: WHITE,
              // Paints out across the page gutter, so the featured row's hover
              // tint and scroll arrow — both hang past the column edge — pass
              // under the pinned strip instead of showing beside it. A clipped
              // shadow rather than negative margins keeps the row in the column.
              ...gutterBleed(stripGutter, WHITE),
              containerType: 'inline-size',
              pt: 1.2,
              pb: isMobile ? 0 : 1.6,
              borderBottom: isMobile ? `1px solid ${BEIGE_600}` : 'none',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between',
                gap: isMobile ? 0 : 1.6,
                '@container (max-width: 600px)': isMobile ? undefined : { flexDirection: 'column', alignItems: 'stretch', gap: 1.2 },
              }}
            >

              <Box
                ref={searchSlotRef}
                data-cy="pf-revamp-search-slot"
                data-hidden={isSearchHidden ? 'true' : 'false'}
                sx={{
                  order: isMobile ? -1 : 1,
                  flex: '0 0 auto',
                  minWidth: 0,
                  opacity: isSearchHidden ? 0 : 1,
                  // Room for the field's 2px focus ring.
                  p: isMobile ? '0.2rem' : 0,
                  transition: 'opacity 140ms ease',
                  '@container (max-width: 600px)': { order: -1 },
                }}
              >
                <Box sx={{ minHeight: 0, '@container (max-width: 600px)': { '& > div': { width: '100%' } } }}>
                  <FuzzySearchBar fullWidth={isMobile} />
                </Box>
              </Box>

              {navCategories.length > 0 &&
                <Box
                  sx={{
                    flex: '0 1 auto',
                    minWidth: 0,
                    // On mobile the cards run to the screen edge; the gutter moves inside the scroller.
                    mx: isMobile ? -1.6 : 0,
                    pt: isMobile ? 0.8 : 0,
                    pb: isMobile ? 1.2 : 0,
                  }}
                >
                  <CategoryRadioNav
                    categories={navCategories}
                    activeCategoryId={activeCategoryId}
                    onSelectCategory={scrollToCategory}
                    showArrows={!isMobile}
                    inlinePadding={isMobile ? '1.6rem' : '0.4rem'}
                  />
                </Box>
              }

            </Box>
          </Box>

          <Box ref={scrollContainerRef}>

            {showSessionError &&
              <Stack alignItems="center" gap={2} py={6} data-cy="pf-revamp-session-error">

                <Alert severity="error" sx={{ width: '100%' }}>
                  {canCompleteProperty
                    ? t('purchase_flow_revamp:drawer.session_property_incomplete_error')
                    : t('purchase_flow_revamp:catalog.session_error')}
                </Alert>

                <MUIButton size="md" onClick={canCompleteProperty ? () => setIsCompletionDismissed(false) : handleRetrySession}>
                  {canCompleteProperty
                    ? t('purchase_flow_revamp:drawer.complete_property_title')
                    : t('purchase_flow_revamp:catalog.session_error_retry')}
                </MUIButton>

              </Stack>
            }

            {isError &&
              <Box mb={2}>
                <PfRetryAlert
                  data-cy="pf-revamp-catalogue-error"
                  message={t('purchase_flow_revamp:catalog.load_error')}
                  onRetry={() => { catalogueQuery.refetch() }}
                />
              </Box>
            }

            {isLoading &&
              <Grid container spacing={3}>
                {Array.from({ length: 6 }).map((_, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={`skeleton-${idx}`}>

                    <ProductCardSkeleton />

                  </Grid>
                ))}
              </Grid>
            }

            {!showSessionError && !isLoading && !isError && filtered.totalProducts === 0 &&
              <Stack alignItems="center" py={6}>

                <Typography variant="text-sm" color={GRAY_700}>
                  {filtered.isSearchActive
                    ? t('purchase_flow_revamp:catalog.empty_search')
                    : t('purchase_flow_revamp:catalog.empty_catalogue')}
                </Typography>

              </Stack>
            }

            {!showSessionError && !isLoading && !isError && filtered.totalProducts > 0 &&
              <Stack gap={isMobile ? 5.6 : 3.4} pb={6.4}>

                {/* Packs first: the POC puts the Featured carousel above the
                    categories. While a search is active it keeps only the
                    matching bundles. */}
                {hasFeatured &&
                  <Box id={`cat-${FEATURED_NAV_ID}`} sx={{ scrollMarginTop: CATEGORY_ANCHOR_SCROLL_MARGIN }}>
                    <FeaturedBundleSection
                      entries={filtered.featured}
                      onSelect={handleSelectFeatured}
                    />
                  </Box>
                }

                {filtered.categories.map((category) => (
                  <CategorySection
                    key={category.id}
                    id={category.id}
                    title={category.displayName}
                    products={category.products}
                    onSelectProduct={handleSelectProduct}
                    onShowProductInfo={setInfoProduct}
                    scrollMarginTop={CATEGORY_ANCHOR_SCROLL_MARGIN}
                  />
                ))}
              </Stack>
            }

          </Box>

        </Stack>

      </Box>

      <ProductDrawer />

      <ProductInfoDrawer product={infoProduct} onClose={() => setInfoProduct(null)} />

      {/* Entry recovery: no property (typically a reload). `proceedWithProperty`
          sets the property before calling `onClose`, so a property present here
          means the user picked one — only a genuine dismiss leaves the flow. */}
      <PropertySelectionDrawer
        isOpen={entryRecovery.isOpen}
        onClose={entryRecovery.onClose}
        onProceed={entryRecovery.onProceed}
      />

      <PropertySelectionDrawer
        isOpen={canCompleteProperty && !isCompletionDismissed}
        onClose={() => undefined}
        onDismiss={() => setIsCompletionDismissed(true)}
        onProceed={handleCompletionProceed}
        preselectedProperty={preselectedProperty}
      />

    </StepShell>
  )
}
